import * as braze from '@braze/web-sdk';
import { BRAZE_CUSTOM_EVENT_NAME } from '~/constants/braze';
import { getBrazeExternalId } from '~/utils/brazeExternalId';
import type { PromoData } from '~/utils/gtmUtils';

let isInitialized = false;

export const brazeInit = (apiKey: string, baseUrl: string) => {
  if (isInitialized) {
    return;
  }
  braze.initialize(apiKey, {
    baseUrl,
    // Banners and HTML in-app messages are both server-authored HTML - the
    // SDK no-ops rendering either of them unless this is explicitly opted in.
    allowUserSuppliedJavascript: true,
    enableSdkAuthentication: true,
  });
  braze.openSession();
  isInitialized = true;

  // The SDK otherwise only sends logged events on its periodic batch timer (3-10s), so any
  // event logged shortly before the page unloads can be silently lost. `visibilitychange` is
  // the reliable cross-browser signal for this (fires on tab close, backgrounding on mobile,
  // and navigation) - `pagehide` is added alongside it to also cover bfcache eviction, which
  // `visibilitychange` alone can miss. Both call the same flush, but each only fires once per
  // unload so there's no risk of double-flushing in a way that matters.
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        braze.requestImmediateDataFlush();
      }
    });
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => {
      braze.requestImmediateDataFlush();
    });
  }
};

export const identifyBrazeUser = (email: string, sdkAuthSignature?: string) => {
  const hashedId = getBrazeExternalId(email);
  braze.changeUser(hashedId, sdkAuthSignature);
};

/**
 * Resets the current visitor to a new anonymous user, clearing the previously identified
 * customer's data from local storage. Call this on logout so a shared/kiosk device doesn't
 * keep attributing the next visitor's activity to whoever was last signed in - `braze.logout()`
 * would do this too, but it also permanently disables the SDK until `enableSDK` is called, which
 * would break banners and in-app messages for that next anonymous visitor.
 */
export const wipeBrazeData = () => {
  braze.wipeData();
};

export const setBrazeSdkAuthenticationSignature = (signature: string) => {
  braze.setSdkAuthenticationSignature(signature);
};

export const subscribeToSdkAuthenticationFailures = (
  subscriber: (error: { errorCode: number; reason?: string; userId?: string; signature?: string }) => void,
): string | undefined => {
  return braze.subscribeToSdkAuthenticationFailures(subscriber);
};

type BannerClickPayload = {
  messageType: 'banner' | 'in_app_message';
  messageName: string;
  buttonName: string;
  destinationUrl?: string;
};

export const logBannerInAppClickEvent = ({
  messageType,
  messageName,
  buttonName,
  destinationUrl,
}: BannerClickPayload) => {
  braze.logCustomEvent(BRAZE_CUSTOM_EVENT_NAME.BANNER_INAPP_CLICKED, {
    message_type: messageType,
    message_name: messageName,
    button_name: buttonName,
    destination_url: destinationUrl,
    page_location: typeof window !== 'undefined' ? window.location.pathname : undefined,
  });

  // The clicked CTA typically navigates the page away immediately, but the SDK otherwise
  // only sends logged events on its periodic batch timer (3-10s) with no beforeunload/beacon
  // fallback - without forcing a flush here, the click event is often still queued (and lost)
  // by the time the browser unloads the page. This also flushes the `logBannerClick` call
  // that precedes this one in `attachBannerClickTracking`.
  braze.requestImmediateDataFlush();
};

export type BrazeBanner = braze.Banner;

/**
 * The banner Braze currently holds for `placementId`. `insertBanner` can render a cached
 * placeholder first and swap in a fresher payload later (see `insertBannerIntoContainer`),
 * so anything read off the banner after the initial insert should come from here rather
 * than the banner captured at insert time.
 */
export const getCurrentBanner = (placementId: string): braze.Banner | null | undefined => braze.getBanner(placementId);

export const getBannerPromoData = (banner: braze.Banner): PromoData | null => {
  const crmPromoData = {
    promo_campaign: banner.getStringProperty('promo_campaign'),
    promo_creative: banner.getStringProperty('promo_creative'),
    promo_placement: banner.getStringProperty('promo_placement'),
  };

  if (Object.values(crmPromoData).every((value) => value === null)) {
    return null;
  }

  return { ...crmPromoData, promo_format: 'banner' };
};

export const removeBrazeSubscription = (subscriptionId: string | undefined) => {
  if (subscriptionId) {
    braze.removeSubscription(subscriptionId);
  }
};

export const subscribeToBannersUpdates = (
  subscriber: (bannersByPlacementId: Record<string, braze.Banner | null>) => void,
): string | undefined => {
  return braze.subscribeToBannersUpdates(subscriber);
};

/**
 * Requests fresh banners for `placementIds`. The SDK locally rate-limits how
 * often it will hit `/banners/sync` at all - when a call arrives too soon
 * after a previous one, it invokes `onError` immediately and synchronously,
 * without ever sending a network request, so `onSuccess` and `onError` must
 * be kept distinct: a caller that treats both the same has no way to tell a
 * real fetch apart from one the SDK silently declined to make.
 */
export const requestBannersRefresh = (placementIds: string[], onSuccess?: () => void, onError?: () => void) => {
  braze.requestBannersRefresh(placementIds, onSuccess, onError);
};

const CTA_BUTTON_NAMES = ['banner_cta_1', 'banner_cta_2'] as const;

type BannerLinkClickHandler = (banner: braze.Banner) => void;

/**
 * Braze renders a banner inside a same-origin `<iframe srcdoc>` whose content
 * sizes itself to fit its own text/image rather than the iframe's viewport -
 * so a banner only ever covers its content's natural height, leaving blank
 * space below it whenever the placement's container is taller than the
 * content (see `BrazeBannerPlacement`, which forces the iframe *element*
 * itself to fill the container - this reaches inside the iframe's own
 * document to make its content do the same). `.bz-banner` is CRM's usual
 * convention for the root element, so it's stretched and its own
 * `background-image` (if any) is forced to cover - but the actual template
 * markup is authored per-campaign and not guaranteed to match that
 * convention or to be flat, so `<img>` content is handled independently of
 * any assumed class name.
 *
 * A `width/height: 100%` on an `<img>` only resolves against a *definite*
 * size, and percentage sizing stops being definite the moment any ancestor
 * has an auto/content-based height - so on a template that wraps the image
 * in intermediate divs (e.g. a "landscape" banner with an overlaid CTA,
 * which typically needs its own positioned wrapper around the image for the
 * overlay to sit on top of), the image silently falls back to its natural
 * size and overflows past the container's bottom edge. `body *:has(img)`
 * force-stretches every ancestor of every image, at any depth, keeping that
 * chain definite regardless of the template's actual structure.
 * `display: block` has to be forced alongside width/height, not just for
 * the image itself but for every one of those ancestors too - width/height do nothing
 * on a non-replaced *inline* box (the CSS default for an unstyled element,
 * e.g. a bare `<a>` wrapping the whole banner), so without it the chain
 * silently stays auto-sized no matter what width/height say. This also
 * neutralizes any `aspect-ratio` on those ancestors: a template built for a
 * specific authored canvas (e.g. a fixed "1200x300" campaign creative) would
 * otherwise size itself from that assumed canvas rather than the real
 * container, which only matches by coincidence and otherwise leaves a gap
 * (or overflow) the exact size of the mismatch. Width has to be forced
 * alongside height for the same reason on the *image* itself: taking it out
 * of flow (below) removes it as the thing that gives an auto-width wrapper
 * (e.g. a bare `<a>` wrapping just the image) any width at all, and an
 * absolutely-positioned image stretched to a zero-width containing block is
 * invisible even though it's correctly "sized" to fill it. The image itself
 * is then taken out of flow with `position: absolute; inset: 0` -
 * but only its *direct* parent is a safe positioning anchor, since the
 * template may already have its own positioned wrapper somewhere higher up
 * (e.g. for that same CTA overlay) that isn't stretched to the full height;
 * `*:has(> img)` makes the direct parent positioned explicitly so `inset: 0`
 * always pins to a box that's actually full height, not whatever the
 * nearest incidentally-positioned ancestor happens to be.
 *
 * Taking the image out of flow like that assumes it *is* the banner's entire
 * visual content, which only holds when there's exactly one of them - a
 * template with more than one (e.g. a logo beside a hero image, or separate
 * desktop/mobile art) would have every one of them stretched to fill and
 * stacked on top of each other at `inset: 0`. Since that can't be told apart
 * from the single-image case by markup convention either,
 * `position: absolute` is only applied to the image(s) when the document
 * actually contains exactly one `<img>` - a multi-image template falls back to its
 * own authored layout for the image itself, though its ancestor chain is
 * still stretched unconditionally (that part only affects wrapper elements,
 * never the images, so it's always safe).
 */
const injectBannerIframeStyle = (iframe: HTMLIFrameElement): void => {
  try {
    const doc = iframe.contentDocument;
    if (!doc?.head) {
      return;
    }
    const rules = [
      'html, body { height: 100%; margin: 0; }',
      '.bz-banner { height: 100% !important; background-size: cover !important; background-position: center !important; }',
      'body *:has(img) { display: block !important; width: 100% !important; height: 100% !important; aspect-ratio: auto !important; }',
    ];
    if (doc.querySelectorAll('img').length === 1) {
      rules.push(
        '*:has(> img) { position: relative !important; }',
        'img { position: absolute !important; inset: 0 !important; width: 100% !important; height: 100% !important; object-fit: cover !important; }',
      );
    }
    const style = doc.createElement('style');
    style.textContent = rules.join(' ');
    doc.head.appendChild(style);
  } catch {
    // Sandboxed or cross-origin iframe - nothing we can reach from the host page.
  }
};

/**
 * Braze renders the banner's content inside the `<iframe srcdoc>`'s own
 * document, not `container` itself - clicks on the banner's `<a>` tags never
 * bubble out to `container`, so tracking has to be wired up on
 * `iframe.contentDocument` instead. Returns a function that removes the
 * listener it attached, or a no-op if it couldn't attach one.
 */
const attachBannerClickTracking = (
  iframe: HTMLIFrameElement,
  placementId: string,
  banner: braze.Banner,
  onBannerLinkClick?: BannerLinkClickHandler,
): (() => void) => {
  try {
    const doc = iframe.contentDocument;
    if (!doc?.body) {
      return () => {};
    }

    const handleClick = (event: Event) => {
      const link = (event.target as HTMLElement | null)?.closest('a');
      if (!link) {
        return;
      }

      // Unlike the Braze CTA events below, which only cover the first two links, a promo
      // click counts any link in the banner.
      onBannerLinkClick?.(banner);

      // Banners are raw HTML authored by CRM in Braze's composer, with no structured
      // `buttons` array like in-app messages have - so banner_cta_1/banner_cta_2 is
      // inferred from the clicked link's position among the banner document's own links.
      const links = Array.from(doc.querySelectorAll('a'));
      const buttonName = CTA_BUTTON_NAMES[links.indexOf(link)];
      if (!buttonName) {
        return;
      }

      // Braze only logs its own "Click" analytics event automatically when the banner's
      // composer-authored HTML calls the `brazeBridge` it's handed via the iframe - a raw
      // custom banner has no such script, so we log it ourselves whenever we detect a click.
      braze.logBannerClick(banner, buttonName);

      logBannerInAppClickEvent({
        messageType: 'banner',
        messageName: placementId,
        buttonName,
        destinationUrl: link.href,
      });
    };

    doc.addEventListener('click', handleClick);
    return () => doc.removeEventListener('click', handleClick);
  } catch {
    // Sandboxed or cross-origin iframe - nothing we can reach from the host page.
    return () => {};
  }
};

/**
 * Wires up style injection and click tracking for `iframe`, tearing down
 * whatever was previously wired up first. Returns the teardown function for
 * this iframe specifically.
 */
const attachToBannerIframe = (
  iframe: HTMLIFrameElement,
  placementId: string,
  banner: braze.Banner | null | undefined,
  onBannerLinkClick?: BannerLinkClickHandler,
): (() => void) => {
  // The srcdoc document isn't necessarily ready yet when the iframe is
  // inserted - try immediately in case it already is, and again once it
  // finishes loading (a fresh `contentDocument` replaces the placeholder).
  injectBannerIframeStyle(iframe);
  const handleStyleLoad = () => injectBannerIframeStyle(iframe);
  iframe.addEventListener('load', handleStyleLoad);

  let removeClickTracking = () => {};
  let handleClickTrackingLoad: (() => void) | undefined;

  if (banner && !banner.isControl) {
    removeClickTracking = attachBannerClickTracking(iframe, placementId, banner, onBannerLinkClick);
    handleClickTrackingLoad = () => {
      removeClickTracking();
      removeClickTracking = attachBannerClickTracking(iframe, placementId, banner, onBannerLinkClick);
    };
    iframe.addEventListener('load', handleClickTrackingLoad);
  }

  return () => {
    iframe.removeEventListener('load', handleStyleLoad);
    if (handleClickTrackingLoad) {
      iframe.removeEventListener('load', handleClickTrackingLoad);
    }
    removeClickTracking();
  };
};

/**
 * Renders the banner assigned to `placementId` into `container` and wires up
 * click tracking. Always inserts the banner (even an A/B holdout control),
 * since Braze only counts an impression once `insertBanner` has run - the
 * caller is responsible for hiding the container when `banner.isControl`.
 *
 * `braze.insertBanner` doesn't just insert once - it subscribes to banner
 * updates *internally* and silently replaces `container`'s `<iframe>` with a
 * brand new one whenever a fresher payload arrives (the very first render can
 * be a cached/placeholder banner that gets swapped for the "real" one a
 * moment later). Without watching for that swap, our style injection and
 * click tracking stay wired to the original, now-detached iframe: the
 * replacement reverts to Braze's un-stretched default sizing (so the banner
 * looks fine at first, then its content stops filling the placement) with no
 * click tracking. A `MutationObserver` re-attaches to whichever iframe is
 * actually mounted whenever this happens.
 *
 * Returns a `cleanup` function that removes every listener this registered -
 * the caller must invoke it when `container` is discarded (e.g. from a
 * callback ref's cleanup), or a remount onto the same node will stack
 * duplicate listeners and double up tracked click events.
 */
export const insertBannerIntoContainer = (
  placementId: string,
  container: HTMLElement,
  onBannerLinkClick?: BannerLinkClickHandler,
): { banner: braze.Banner | null | undefined; cleanup: () => void } => {
  const banner = braze.getBanner(placementId);
  braze.insertBanner(banner, container);

  let detachFromCurrentIframe = () => {};
  let currentIframe: HTMLIFrameElement | null = null;

  const attachIfNewIframe = () => {
    const iframe = container.querySelector('iframe');
    if (!iframe || iframe === currentIframe) {
      return;
    }
    detachFromCurrentIframe();
    currentIframe = iframe;
    detachFromCurrentIframe = attachToBannerIframe(iframe, placementId, banner, onBannerLinkClick);
  };

  attachIfNewIframe();

  const observer = new MutationObserver(attachIfNewIframe);
  observer.observe(container, { childList: true });

  return {
    banner,
    cleanup: () => {
      observer.disconnect();
      detachFromCurrentIframe();
    },
  };
};

export const subscribeToInAppMessages = () => {
  return braze.subscribeToInAppMessage((message) => {
    if (message instanceof braze.ControlMessage) {
      braze.showInAppMessage(message);
      return;
    }

    // `extras.message_name` lets CRM tag the message with a stable
    // identifier, mirroring the existing `extras.page_name` convention this
    // app already uses for Braze content targeting. Falls back to
    // `triggerId`, which the SDK always reports.
    const messageName = message.extras?.message_name ?? message.triggerId ?? 'in_app_message';

    if (message instanceof braze.ModalMessage || message instanceof braze.FullScreenMessage) {
      message.buttons.forEach((button, index) => {
        const buttonName = CTA_BUTTON_NAMES[index];
        if (!buttonName) {
          return;
        }

        button.subscribeToClickedEvent(() => {
          logBannerInAppClickEvent({
            messageType: 'in_app_message',
            messageName,
            buttonName,
            destinationUrl: button.uri,
          });
        });
      });
    } else if (message instanceof braze.SlideUpMessage) {
      // Slideup messages have a single click action rather than a `buttons`
      // array - track the message's own click as banner_cta_1.
      message.subscribeToClickedEvent(() => {
        logBannerInAppClickEvent({
          messageType: 'in_app_message',
          messageName,
          buttonName: CTA_BUTTON_NAMES[0],
          destinationUrl: message.uri,
        });
      });
    }

    braze.showInAppMessage(message);
  });
};
